// src/app/shared/directives/cell-copy.directive.ts
import { Clipboard } from '@angular/cdk/clipboard';
import { Directive, ElementRef, HostListener } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CellContextMenuService } from 'src/app/core/services/cell-context-menu.service';
import { NotificationService } from 'src/app/core/services/notification.service';

/** Elements whose own context menu (cut/copy/paste, form controls) must never be hijacked.
 *  Includes ARIA buttons ([role="button"]) so a clickable custom column (a mat-icon or span
 *  wired with (click), not a native <button>) keeps its native menu instead of copying. */
const INTERACTIVE = 'input, textarea, select, button, [role="button"], [contenteditable]';

/** The right mouse button, the one that opens the context menu. */
const RIGHT_BUTTON = 2;

/**
 * Right-click on a table cell copies its visible text, when the user's
 * `display.cellRightClick` preference says so. Shift+right-click always falls through to
 * the browser menu, as do empty cells and interactive targets. Hosted on the
 * `.cell-copy-host` wrapper span (display:contents) that wraps <app-column> — not on
 * <app-column> itself, whose rendered column view is a sibling, nor on <mat-cell>, which
 * also holds the mobile-label and sort-badge text. The wrapper's innerText is exactly the
 * rendered cell value.
 *
 * **The copy runs on `mousedown`, not on `contextmenu`.** WebKit only treats the right-button
 * mousedown as clipboard-capable user activation; by the time the paired `contextmenu` handler
 * runs the activation is spent, so in Safari EVERY copy mechanism is refused there — CDK's
 * `execCommand` returns false (and its internal `catch {}` discards the reason, which is why the
 * old code produced a silent "copy failed" toast) and `navigator.clipboard.writeText` rejects with
 * `NotAllowedError`. Chrome and Firefox accept both events, so copying on mousedown is correct
 * everywhere. `contextmenu` is then left with one job: suppress the menu for a copy that already
 * happened.
 *
 * A `contextmenu` with no preceding right-button mousedown — the keyboard menu key — still tries
 * to copy, since that path has no mousedown to hang off.
 *
 * When the copy is refused the native menu is deliberately allowed through: it carries a working
 * Copy item, which serves the user better than an error toast they can do nothing about.
 *
 * Holds no subscription — the preference is read per event — so one instance per rendered
 * cell is cheap.
 */
@Directive({ selector: '[appCellCopy]', standalone: true })
export class CellCopyDirective {
  /** Set when the right-button mousedown already copied, so the paired contextmenu only suppresses. */
  private copiedOnMouseDown = false;

  constructor(
    private host: ElementRef<HTMLElement>,
    private cellContextMenu: CellContextMenuService,
    private clipboard: Clipboard,
    private notification: NotificationService,
    private translate: TranslateService,
  ) {}

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent): void {
    // Reset first: a press that does not copy must not leave a stale flag suppressing a later menu.
    this.copiedOnMouseDown = false;
    if (event.button !== RIGHT_BUTTON) return;

    const text = this.eligibleText(event);
    if (text === null) return;

    this.copiedOnMouseDown = this.copy(text);
  }

  @HostListener('contextmenu', ['$event'])
  onContextMenu(event: MouseEvent): void {
    if (this.copiedOnMouseDown) {
      this.copiedOnMouseDown = false;
      event.preventDefault();
      return;
    }

    // No mousedown preceded this (keyboard menu key), so this is the only chance to copy.
    const text = this.eligibleText(event);
    if (text === null) return;

    if (this.copy(text)) {
      event.preventDefault();
    }
  }

  /** The cell text to copy, or null when this event must fall through to the native menu. */
  private eligibleText(event: MouseEvent): string | null {
    if (this.cellContextMenu.current !== 'copy') return null;
    if (event.shiftKey) return null;
    if ((event.target as Element | null)?.closest(INTERACTIVE)) return null;

    return this.host.nativeElement.innerText.trim() || null;
  }

  /** Copy, snacking only on success. Returns whether the clipboard actually took the text. */
  private copy(text: string): boolean {
    if (!this.clipboard.copy(text)) return false;

    this.notification.success(this.translate.instant('common.copiedToClipboard'));
    return true;
  }
}
